import { BIBLIO } from '@somnhold/content';
import { cellAtWorld, cellCenter, cellId, heroWalkable, touchGrid } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { dist2, iatan2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { BiblioFoe, BPt, BookSealRules, SealsState, SootRules, SootState } from '../biblio-types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { sendaCue } from './senda.ts';
import type { CabinetLayout, LecternLayout, SootLayout } from './biblio-layout.ts';

/**
 * The burnt library (docs/sendas/mago.md, chapter 1; the grounds in battle/biblio-layout.ts).
 *
 * The cellars (act 1, `soot`): four halls down to the catalog. Piles of ash rise as the hero comes near (a skeleton, an
 * apparition; the page in its hand glows before, `near`); while one risen of a hall stands, the fallen beams block its
 * stair down, and the one it came in by once the hero is down in it (what woke is put down before going on). Loose
 * pages come off the burning shelves of the hero's hall and go for it. Won at the catalog.
 *
 * The locks (acts 2 and 3, `seals`, §4 «Los cerrojos»): each seal breaks to its reaction, with the hero's own bolt
 * (lightning) counted on it: frost and then lightning (shatter); acid and then fire (combustion); ink only with holy or
 * shadow first, or a scribe killed writing on it (its ink undoes it). By each seal a frost bowl, an acid flask and a
 * brazier break to a bolt from up close and leave their state on it, back a while later. What goes wrong is seen: fire
 * melts a seal's frost, lightning burns its acid off, a brazier with no acid under it is spent. A tap on a mark sends the
 * hero to bolt it (`sealOrder`; `sealShot`, from the hero's own turn, fires it with its attack). A scribe goes to the desk
 * (the ink lectern, the cabinet) and writes `write` (3 s): finished, it seals again the last seal broken, and writes
 * again; on the way it writes runes under the hero. In the fire room the walls of fire rise in their fixed turn on the
 * spokes between the shelves (the floor red before): caught on one, a big blow; by one up, the heat. Won with the three
 * broken at once; the test lost at `lose` seals sealed again.
 *
 * Cost: the seals and items are a handful; skills are read off the tick's own events and the zones once a second.
 */

/* ---------- builders ---------- */

/** A foe's life and blow at the chapter's toughness: `d` seconds of D (`life`), `heroBp` of the hero's life. */
const foeOf = (f: { d: number; heroBp: number }, life: (d: number) => number, tough: number, act: { hpBp: number; dmgBp: number }): BiblioFoe => ({
  hp: imax(1, mulBp(mulBp(life(f.d), tough), act.hpBp)),
  heroBp: mulBp(mulBp(f.heroBp, tough), act.dmgBp),
});

export const sootRules = (L: SootLayout, radius: number, life: (d: number) => number, tough: number): SootRules => {
  const S = BIBLIO.soot;
  return {
    halls: L.halls.map((h) => ({
      at: { ...h.at },
      stair: h.stair.map((c) => cellId(radius, c.q, c.r)).sort((a, b) => a - b),
      piles: h.piles.map((p) => ({ at: { x: p.x, y: p.y }, kind: p.kind })),
      shelves: h.shelves.map((p) => ({ ...p })),
    })),
    catalog: { ...L.catalog },
    reach: S.reach,
    wake: S.wake,
    glow: S.glow,
    pages: { ...S.pages },
    page: { ...foeOf(BIBLIO.page, life, tough, S), reload: BIBLIO.page.reload },
    skeleton: foeOf(BIBLIO.skeleton, life, tough, S),
    wraith: foeOf(BIBLIO.wraith, life, tough, S),
  };
};

const scribeOf = (life: (d: number) => number, tough: number, act: { hpBp: number; dmgBp: number }): BookSealRules['scribe'] => {
  const S = BIBLIO.scribe;
  return {
    ...foeOf(S, life, tough, act),
    pace: S.pace,
    write: S.write,
    rest: S.rest,
    rune: { ...S.rune },
  };
};

export const lecternRules = (L: LecternLayout, life: (d: number) => number, tough: number): BookSealRules => {
  const A = BIBLIO.locks;
  const ink = L.lecterns.find((l) => l.kind === 2) as LecternLayout['lecterns'][number];
  return {
    cabinet: false,
    seals: L.lecterns.map((l) => ({ kind: l.kind, at: { x: l.x, y: l.y } })),
    items: L.items.map((it) => ({
      kind: it.kind,
      at: { x: it.x, y: it.y },
      seal: it.seal,
    })),
    refill: A.refill,
    itemReach: BIBLIO.item.reach,
    // (The scribe stands at the ink lectern's front, a little up from it: the way it comes in from the door.)
    desk: { x: ink.x, y: ink.y - 900 },
    deskR: 350,
    doors: [{ ...L.door }],
    scribe: scribeOf(life, tough, A),
    page: { ...foeOf(BIBLIO.page, life, tough, A), reload: BIBLIO.page.reload },
    pages: {
      first: A.scribeFirst,
      every: A.pages.every,
      n: A.pages.n,
      from: L.shelves.map((p) => ({ ...p })),
    },
    scribes: { first: A.scribeFirst, every: A.scribeEvery },
    gate: 2,
    lose: 0,
    hold: 0,
  };
};

export const cabinetRules = (L: CabinetLayout, radius: number, life: (d: number) => number, tough: number): BookSealRules => {
  const C = BIBLIO.cabinet;
  return {
    cabinet: true,
    seals: L.shelves.map((sh) => ({
      kind: sh.seal.kind,
      at: { x: sh.seal.x, y: sh.seal.y },
    })),
    items: L.shelves.flatMap((sh, i) =>
      sh.items.map((it) => ({
        kind: it.kind,
        at: { x: it.x, y: it.y },
        seal: i,
      })),
    ),
    refill: C.refill,
    itemReach: BIBLIO.item.reach,
    desk: { x: 0, y: 0 },
    deskR: 1500,
    doors: L.doors.map((d) => ({ ...d })),
    scribe: scribeOf(life, tough, C),
    page: { ...foeOf(BIBLIO.page, life, tough, C), reload: BIBLIO.page.reload },
    pages: {
      first: C.pages.first,
      every: C.pages.every,
      n: C.pages.n,
      from: L.doors.map((d) => ({ ...d })),
    },
    scribes: { first: C.scribeFirst, every: C.scribeEvery },
    gate: 0,
    lose: C.lose,
    hold: C.hold,
    spokes: {
      cells: L.spokes.map((sp) => sp.map((c) => cellId(radius, c.q, c.r)).sort((a, b) => a - b)),
      pts: L.spokes.map((sp) => sp.map((c) => hexToWorld(c.q, c.r))),
      first: C.spokes.first,
      gap: C.spokes.gap,
      up: C.spokes.up,
      warn: C.spokes.warn,
      caughtBp: C.spokes.caughtBp,
      heatBp: C.spokes.heatBp,
      heatR: C.spokes.heatR,
      smokeBp: C.spokes.smokeBp,
    },
  };
};

/* ---------- common ---------- */

/** One of the library's nightmares: its own life and blow (a share of the hero's life). */
const foe = (s: BattleState, kind: 'crow' | 'skeleton' | 'wraith' | 'scribe', x: number, y: number, f: BiblioFoe): Enemy => {
  const e = spawnEnemy(s, kind, 1, false, 0, x, y, f.hp);
  const h = s.heroes[0];
  if (h) e.damage = imax(1, mulBp(h.stats.maxHp, f.heroBp));
  return e;
};

const hero = (s: BattleState): Hero | undefined => s.heroes.find((x) => x.downTicks === 0);

const plugsOf = (cells: number[][], on: (i: number) => boolean): number[] => {
  const out: number[] = [];
  cells.forEach((c, i) => {
    if (on(i)) out.push(...c);
  });
  return out.sort((a, b) => a - b);
};

/* ---------- the cellars ---------- */

const freshSoot = (r: SootRules): SootState => ({
  hall: 0,
  risen: r.halls.map((h) => h.piles.map(() => 0)),
  shut: r.halls.map(() => false),
  nextPage: r.pages.first,
  flocks: 0,
  near: 0,
});

/** Risen of a hall still standing. */
const upIn = (s: BattleState, w: SootState, i: number): number => {
  let n = 0;
  for (const id of w.risen[i] ?? []) {
    if (!id) continue;
    const e = findEnemy(s, id);
    if (e && e.hp > 0) n++;
  }
  return n;
};

export const sootTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.soot;
  const st = s.senda;
  if (!r || !st) return;
  st.soot ??= freshSoot(r);
  const w = st.soot;
  if (!s.started) return;
  const h = hero(s);
  if (h) {
    let best = 0;
    let bd = 0x7fffffff;
    r.halls.forEach((hl, i) => {
      const d = Math.abs(hl.at.y - h.y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    if (best > w.hall) {
      w.hall = best;
      cue(`hall${best + 1}`);
    }
    // The piles of the hero's hall rise as it comes near.
    const hl = r.halls[best];
    hl?.piles.forEach((p, k) => {
      const row = w.risen[best] as number[];
      if (row[k] || !withinRange(h.x, h.y, p.at.x, p.at.y, r.wake)) return;
      const e = foe(s, p.kind === 0 ? 'skeleton' : 'wraith', p.at.x, p.at.y, p.kind === 0 ? r.skeleton : r.wraith);
      row[k] = e.id;
      ev(s, {
        t: 'senda',
        kind: 'rise',
        npc: '',
        x: p.at.x,
        y: p.at.y,
        n: best * 8 + k,
      });
      cue(`rise${best + 1}`);
    });
    // The page glows with the nearest nightmare.
    let nd = r.glow * r.glow + 1;
    for (const e of s.enemies) if (e.hp > 0) nd = imin(nd, dist2(e.x, e.y, h.x, h.y));
    w.near = nd <= r.glow * r.glow ? imax(1, isqrt(nd)) : 0;
  }
  // The beams: down over a hall's stair while one of its risen stands, and over the one above once the hero is down.
  let changed = false;
  r.halls.forEach((hl, i) => {
    if (!hl.stair.length) return;
    let shut = upIn(s, w, i) > 0 || (upIn(s, w, i + 1) > 0 && w.hall > i);
    // (Never down on the hero: on the stair it goes on through.)
    if (shut && h && hl.stair.some((c) => withinRange(h.x, h.y, cellCenter(s.map.radius, c).x, cellCenter(s.map.radius, c).y, 1300))) shut = w.shut[i] as boolean;
    if (shut === w.shut[i]) return;
    w.shut[i] = shut;
    changed = true;
    const c = cellCenter(s.map.radius, hl.stair[0] as number);
    ev(s, {
      t: 'senda',
      kind: 'beams',
      npc: '',
      x: c.x,
      y: c.y,
      n: i * 2 + (shut ? 1 : 0),
    });
    if (shut) cue(`beams${i + 1}`);
  });
  if (changed) {
    s.plugs = plugsOf(
      r.halls.map((x) => x.stair),
      (i) => !!w.shut[i],
    );
    touchGrid(s);
  }
  // Loose pages off a burning shelf of the hero's hall.
  if (h && s.waveT >= w.nextPage) {
    w.nextPage = s.waveT + r.pages.every;
    const hl = r.halls[w.hall];
    const sh = hl?.shelves[rngInt(s.rng, hl.shelves.length)];
    if (sh) {
      for (let k = 0; k < r.pages.n; k++) {
        const e = foe(s, 'crow', sh.x + (k % 2) * 300, sh.y + idiv(k, 2) * 300, r.page);
        e.reload = r.page.reload;
      }
      w.flocks++;
      ev(s, {
        t: 'senda',
        kind: 'flock',
        npc: '',
        x: sh.x,
        y: sh.y,
        n: w.flocks,
      });
      cue(`pages${w.flocks}`);
    }
  }
};

/** Halls gone down through (of four). */
export const sootGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.soot;
  const w = s.senda?.soot;
  if (!r || !w) return { done: 0, need: 1 };
  return {
    done: w.hall + (sootResult(s) === 'won' ? 1 : 0),
    need: r.halls.length,
  };
};

export const sootResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.soot;
  const h = hero(s);
  return r && h && withinRange(h.x, h.y, r.catalog.x, r.catalog.y, r.reach) ? 'won' : null;
};

/* ---------- the locks ---------- */

/** What touches a seal. */
type Touch = 'frost' | 'acid' | 'fire' | 'lightning';
const ITEM_TOUCH: Touch[] = ['frost', 'acid', 'fire'];

const freshSeals = (r: BookSealRules, s: BattleState): SealsState => ({
  seals: r.seals.map(() => ({ prep: 0, broken: false })),
  back: r.items.map(() => 0),
  job: -1,
  scribes: [],
  runes: [],
  from: r.gate === 0 ? s.waveT : -1,
  nextPage: r.gate === 0 ? r.pages.first : 0x7fffffff,
  nextScribe: r.gate === 0 ? r.scribes.first : 0x7fffffff,
  resealed: 0,
  broke: 0,
  done: -1,
  up: 0,
  scribeN: 0,
  writes: 0,
});

/**
 * The broken seals, the last broken last. A seal's `prep` is its readying while whole (0 none, 1 frost on a frost seal,
 * 2 acid on a crust seal) and its place in the order broken once broken (the highest, the last).
 */
const brokenOrder = (w: SealsState): number[] =>
  w.seals
    .map((x, i) => ({ x, i }))
    .filter((o) => o.x.broken)
    .sort((a, b) => a.x.prep - b.x.prep)
    .map((o) => o.i);

/** A mark's point: an item, or a seal (after the items). */
export const sealMarkAt = (r: BookSealRules, at: number): BPt | null => (at < r.items.length ? (r.items[at]?.at ?? null) : (r.seals[at - r.items.length]?.at ?? null));

const breakSeal = (s: BattleState, r: BookSealRules, w: SealsState, i: number, cue: (c: string) => void): void => {
  const sl = w.seals[i];
  if (!sl || sl.broken) return;
  sl.broken = true;
  w.broke++;
  // (Its place in the order broken: the highest yet.)
  sl.prep = 1 + w.broke;
  (w.at ??= w.seals.map(() => 0))[i] = s.tick;
  const at = r.seals[i]?.at as BPt;
  ev(s, { t: 'senda', kind: 'seal', npc: '', x: at.x, y: at.y, n: i * 8 + 2 });
  cue(`open${w.seals.filter((x) => x.broken).length}`);
  cue(`seal${w.broke}`);
  if (w.seals.every((x) => x.broken) && w.done < 0) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'opened', npc: '', x: r.desk.x, y: r.desk.y });
    cue('opened');
  }
};

/** Something touches seal `i`: it readies it, breaks it, undoes its readying, or does nothing. */
const touch = (s: BattleState, r: BookSealRules, w: SealsState, i: number, t: Touch | 'ink', cue: (c: string) => void): void => {
  const sl = w.seals[i];
  const def = r.seals[i];
  if (!sl || !def || sl.broken) return;
  const at = def.at;
  const say = (what: number): void =>
    ev(s, {
      t: 'senda',
      kind: 'seal',
      npc: '',
      x: at.x,
      y: at.y,
      n: i * 8 + what,
    });
  if (t === 'ink') {
    if (def.kind === 2) breakSeal(s, r, w, i, cue);
    return;
  }
  if (def.kind === 0) {
    // Frost: lightning on its frost shatters it; fire melts the frost off.
    if (t === 'frost' && sl.prep !== 1) {
      sl.prep = 1;
      say(1);
    } else if (t === 'lightning' && sl.prep === 1) breakSeal(s, r, w, i, cue);
    else if (t === 'fire' && sl.prep === 1) {
      sl.prep = 0;
      say(4);
    } else say(3);
  } else if (def.kind === 1) {
    // Crust: fire on its acid burns it; lightning burns the acid off; fire with nothing under it is spent.
    if (t === 'acid' && sl.prep !== 2) {
      sl.prep = 2;
      say(1);
    } else if (t === 'fire' && sl.prep === 2) breakSeal(s, r, w, i, cue);
    else if (t === 'lightning' && sl.prep === 2) {
      sl.prep = 0;
      say(4);
    } else say(t === 'fire' ? 5 : 3);
  } else say(3);
};

/** The hero sent to bolt a mark (an item, a seal): it walks into reach and fires with its own attack. */
export const sealOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.seals;
  const w = s.senda?.seals;
  if (!r || !w || w.done >= 0 || at < 0 || at >= r.items.length + r.seals.length) return;
  const p = sealMarkAt(r, at) as BPt;
  w.job = at;
  h.target = 0;
  h.forced = false;
  const reach = at < r.items.length ? r.itemReach : h.stats.range;
  if (!withinRange(h.x, h.y, p.x, p.y, reach - 200)) orderMove(s, h, p.x, p.y);
};

/**
 * The hero's own turn (battle/heroes.ts): on a job within reach of its mark it stands, faces it and fires its bolt there
 * with its attack (true: nothing else this turn); not yet in reach, false (it walks on).
 */
export const sealShot = (s: BattleState, h: Hero): boolean => {
  const r = s.rules?.senda?.seals;
  const w = s.senda?.seals;
  if (!r || !w || w.job < 0 || w.done >= 0) return false;
  const at = w.job;
  const p = sealMarkAt(r, at);
  if (!p) return false;
  const item = at < r.items.length;
  if (!withinRange(h.x, h.y, p.x, p.y, item ? r.itemReach : h.stats.range)) return false;
  h.moving = false;
  h.path = [];
  h.target = 0;
  h.facing = iatan2(p.y - h.y, p.x - h.x);
  if (h.attackCd > 0 || (h.fear ?? 0) > 0) return true;
  h.attackCd = h.stats.reload;
  h.anim = 'attack';
  h.animT = 0;
  w.job = -1;
  ev(s, { t: 'senda', kind: 'zap', npc: '', x: p.x, y: p.y, n: at });
  const cue = (c: string): void => sendaCue(s, c);
  if (item) {
    const it = r.items[at];
    if (!it || w.back[at]) return true;
    w.back[at] = s.tick + r.refill;
    ev(s, { t: 'senda', kind: 'item', npc: '', x: p.x, y: p.y, n: at });
    touch(s, r, w, it.seal, ITEM_TOUCH[it.kind] as Touch, cue);
  } else touch(s, r, w, at - r.items.length, 'lightning', cue);
  return true;
};

/** A wall of fire's state at wave time `t`: 0 down, 1 warned (the floor red), 2 up. */
export const spokeState = (sp: NonNullable<BookSealRules['spokes']>, k: number, t: number): 0 | 1 | 2 => {
  const cycle = sp.gap * sp.cells.length;
  const d = t - sp.first - k * sp.gap;
  if (d < -sp.warn) return 0;
  if (d < 0) return 1;
  const ph = d % cycle;
  if (ph < sp.up) return 2;
  return ph >= cycle - sp.warn ? 1 : 0;
};

/** Ticks till spoke `k` rises next (0 up now). */
export const spokeIn = (sp: NonNullable<BookSealRules['spokes']>, k: number, t: number): number => {
  const cycle = sp.gap * sp.cells.length;
  const d = t - sp.first - k * sp.gap;
  if (d < 0) return -d;
  const ph = d % cycle;
  return ph < sp.up ? 0 : cycle - ph;
};

/** The walls of fire: up and down in their turn (the grid's plugs), the hero caught on one rising, the heat by one up. */
const spokesTick = (s: BattleState, r: BookSealRules, w: SealsState, cue: (c: string) => void): void => {
  const sp = r.spokes;
  if (!sp) return;
  let bits = 0;
  for (let k = 0; k < sp.cells.length; k++) {
    const st = spokeState(sp, k, s.waveT);
    if (st === 2) bits |= 1 << k;
    // (The warning once, as it begins.)
    if (st === 1 && spokeState(sp, k, s.waveT - 1) === 0) {
      const p = sp.pts[k]?.[3] ?? { x: 0, y: 0 };
      ev(s, { t: 'senda', kind: 'spokeWarn', npc: '', x: p.x, y: p.y, n: k });
    }
  }
  const h = hero(s);
  if (bits !== w.up) {
    for (let k = 0; k < sp.cells.length; k++) {
      const was = (w.up >> k) & 1;
      const now = (bits >> k) & 1;
      if (was === now) continue;
      const p = sp.pts[k]?.[3] ?? { x: 0, y: 0 };
      ev(s, {
        t: 'senda',
        kind: now ? 'spokeUp' : 'spokeDown',
        npc: '',
        x: p.x,
        y: p.y,
        n: k,
      });
      // Caught on it as it rises: the blow, and out of it to the nearest floor.
      if (now && h) {
        const at = cellAtWorld(s, h.x, h.y);
        if ((sp.cells[k] as number[]).includes(at)) {
          damageHero(s, h, mulBp(h.stats.maxHp, sp.caughtBp), null, 'fire');
          ev(s, { t: 'senda', kind: 'caught', npc: '', x: h.x, y: h.y, n: k });
          cue('caught');
          off(s, h, sp.cells[k] as number[]);
        }
      }
    }
    w.up = bits;
    s.plugs = plugsOf(sp.cells, (k) => ((bits >> k) & 1) === 1);
    touchGrid(s);
  }
  // The smoke, everywhere in the room, once a second (the walls up or not: the fire began here).
  if (h && s.tick % 12 === 6) damageHero(s, h, mulBp(h.stats.maxHp, sp.smokeBp), null, 'fire');
  // The heat by a wall up, once a second.
  if (h && bits && s.tick % 12 === 0) {
    const r2 = sp.heatR * sp.heatR;
    let near = false;
    for (let k = 0; k < sp.pts.length && !near; k++) {
      if (!((bits >> k) & 1)) continue;
      for (const p of sp.pts[k] as BPt[]) if (dist2(p.x, p.y, h.x, h.y) <= r2) near = true;
    }
    if (near) damageHero(s, h, mulBp(h.stats.maxHp, sp.heatBp), null, 'fire');
  }
};

/** Out of a wall of fire: to the nearest floor cell not on it (a step to either side). */
const off = (s: BattleState, h: Hero, cells: number[]): void => {
  const R = s.map.radius;
  const at = cellAtWorld(s, h.x, h.y);
  const c = cellCenter(R, at);
  let best: BPt | null = null;
  let bd = 0x7fffffff;
  for (let dx = -1500; dx <= 1500; dx += 750)
    for (let dy = -1500; dy <= 1500; dy += 750) {
      const id = cellAtWorld(s, c.x + dx, c.y + dy);
      if (id < 0 || cells.includes(id) || !heroWalkable(s, id)) continue;
      const p = cellCenter(R, id);
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
  if (!best) return;
  h.x = h.ax = h.destX = best.x;
  h.y = h.ay = h.destY = best.y;
  h.path = [];
  h.moving = false;
};

/** Skills on the seals (they help, they're never needed): a fireball's burst, frost and storm zones, once a second. */
const skillsOn = (s: BattleState, r: BookSealRules, w: SealsState, cue: (c: string) => void): void => {
  for (const e of s._events ?? []) {
    if (e.t !== 'boom' || e.kind !== 'fireball') continue;
    r.seals.forEach((sl, i) => {
      if (withinRange(sl.at.x, sl.at.y, e.x, e.y, e.r + 600)) touch(s, r, w, i, 'fire', cue);
    });
  }
  if (s.tick % 12 !== 0) return;
  for (const z of s.zones) {
    const t: Touch | null = z.kind === 'frost' || z.kind === 'blizzard' ? 'frost' : z.kind === 'storm' ? 'lightning' : z.kind === 'burn' ? 'fire' : null;
    if (!t || z.pid === '') continue;
    r.seals.forEach((sl, i) => {
      const hit = z.cells.length
        ? z.cells.some((c) => withinRange(cellCenter(s.map.radius, c).x, cellCenter(s.map.radius, c).y, sl.at.x, sl.at.y, 1300))
        : withinRange(sl.at.x, sl.at.y, z.x, z.y, z.radius + 600);
      if (hit) touch(s, r, w, i, t, cue);
    });
  }
};

/** The seal a scribe at the desk writes on: the last broken (none: it writes all the same). */
const lastBroken = (w: SealsState): number => {
  const o = brokenOrder(w);
  return o.length ? (o[o.length - 1] as number) : -1;
};

export const sealsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.seals;
  const st = s.senda;
  if (!r || !st) return;
  st.seals ??= freshSeals(r, s);
  const w = st.seals;
  if (!s.started) return;
  if (w.done >= 0) return;
  skillsOn(s, r, w, cue);
  // The test: a seal broken alone closes again by itself after `hold` (the three must be broken at once).
  if (r.hold)
    w.seals.forEach((x, i) => {
      if (!x.broken || s.tick < (w.at?.[i] ?? 0) + r.hold) return;
      x.broken = false;
      x.prep = 0;
      const at = r.seals[i]?.at as BPt;
      ev(s, { t: 'senda', kind: 'reseal', npc: '', x: at.x, y: at.y, n: i });
      cue('selfSeal');
    });
  // Items back.
  w.back.forEach((b, i) => {
    if (b && s.tick >= b) {
      w.back[i] = 0;
      const it = r.items[i] as BookSealRules['items'][number];
      ev(s, {
        t: 'senda',
        kind: 'refill',
        npc: '',
        x: it.at.x,
        y: it.at.y,
        n: i,
      });
    }
  });
  spokesTick(s, r, w, cue);
  const h = hero(s);
  // A job the hero can't get to any more (it stopped short): off.
  if (h && w.job >= 0 && !h.moving) {
    const p = sealMarkAt(r, w.job) as BPt;
    const reach = w.job < r.items.length ? r.itemReach : h.stats.range;
    if (!withinRange(h.x, h.y, p.x, p.y, reach)) w.job = -1;
  }
  // The pressure: from the start (the test), or once `gate` seals are open (the third lectern).
  if (w.from < 0 && w.seals.filter((x) => x.broken).length >= r.gate) {
    w.from = s.waveT;
    w.nextPage = s.waveT + r.pages.first;
    w.nextScribe = s.waveT + r.scribes.first;
    cue('pressure');
  }
  if (w.from >= 0 && h) {
    if (s.waveT >= w.nextPage) {
      w.nextPage = s.waveT + r.pages.every;
      const at = r.pages.from[rngInt(s.rng, r.pages.from.length)] as BPt;
      for (let k = 0; k < r.pages.n; k++) {
        const e = foe(s, 'crow', at.x + (k % 2) * 300, at.y + idiv(k, 2) * 300, r.page);
        e.reload = r.page.reload;
      }
      ev(s, { t: 'senda', kind: 'flock', npc: '', x: at.x, y: at.y, n: 0 });
    }
    if (s.waveT >= w.nextScribe) {
      w.nextScribe = s.waveT + r.scribes.every;
      const at = r.doors[w.scribeN % r.doors.length] as BPt;
      const e = foe(s, 'scribe', at.x, at.y, r.scribe);
      e.speed = r.scribe.pace * 12;
      w.scribes.push({
        id: e.id,
        mode: 0,
        t: 0,
        rune: s.tick + r.scribe.rune.every,
      });
      w.scribeN++;
      cue(`scribe${w.scribeN}`);
    }
  }
  // The scribes: writing, resting, their death (writing: its ink), their runes on the way.
  for (let i = w.scribes.length - 1; i >= 0; i--) {
    const u = w.scribes[i] as SealsState['scribes'][number];
    const e = findEnemy(s, u.id);
    if (!e || e.hp <= 0) {
      if (u.mode === 1) {
        const ink = r.seals.findIndex((x) => x.kind === 2);
        ev(s, {
          t: 'senda',
          kind: 'inked',
          npc: '',
          x: e?.x ?? r.desk.x,
          y: e?.y ?? r.desk.y,
          n: ink,
        });
        if (ink >= 0) touch(s, r, w, ink, 'ink', cue);
      }
      w.scribes.splice(i, 1);
      continue;
    }
    if (u.mode === 1 && s.tick >= u.t) {
      const k = lastBroken(w);
      if (k >= 0) {
        const sl = w.seals[k] as SealsState['seals'][number];
        sl.broken = false;
        sl.prep = 0;
        w.resealed++;
        const at = r.seals[k]?.at as BPt;
        ev(s, { t: 'senda', kind: 'reseal', npc: '', x: at.x, y: at.y, n: k });
        cue(`reseal${w.resealed}`);
      }
      u.mode = 2;
      u.t = s.tick + r.scribe.rest;
    } else if (u.mode === 2 && s.tick >= u.t && lastBroken(w) >= 0) {
      // (It writes only with a seal to close: with all three whole it waits at the desk.)
      u.mode = 1;
      u.t = s.tick + r.scribe.write;
      ev(s, { t: 'senda', kind: 'write', npc: '', x: e.x, y: e.y, n: u.id });
      cue(`writing${++w.writes}`);
    }
    if (u.mode !== 1 && h && s.tick >= u.rune) {
      u.rune = s.tick + r.scribe.rune.every;
      if (withinRange(e.x, e.y, h.x, h.y, r.scribe.rune.range)) {
        w.runes.push({ x: h.x, y: h.y, at: s.tick + r.scribe.rune.warn });
        e.anim = 'cast';
        e.animT = 0;
        ev(s, {
          t: 'senda',
          kind: 'rune',
          npc: '',
          x: h.x,
          y: h.y,
          n: r.scribe.rune.warn,
        });
      }
    }
  }
  // Runes closing: whoever stands on one is struck and cursed.
  for (let i = w.runes.length - 1; i >= 0; i--) {
    const q = w.runes[i] as SealsState['runes'][number];
    if (s.tick < q.at) continue;
    w.runes.splice(i, 1);
    if (h && withinRange(h.x, h.y, q.x, q.y, r.scribe.rune.r)) {
      damageHero(s, h, mulBp(h.stats.maxHp, r.scribe.heroBp), null, 'shadow');
      h.curse = imax(h.curse ?? 0, r.scribe.rune.curse);
      h.curseBp = r.scribe.rune.healBp;
      ev(s, { t: 'senda', kind: 'runeHit', npc: '', x: q.x, y: q.y });
    }
  }
};

/** A scribe's walk to the desk (from the enemies' turn): it goes there and stands writing, whatever comes. */
export const sealsHold = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda?.seals;
  const w = s.senda?.seals;
  if (!r || !w || e.kind !== 'scribe') return false;
  const u = w.scribes.find((x) => x.id === e.id);
  if (!u) return false;
  if (e.stun > 0) {
    e.stun--;
    e.anim = 'stun';
    return true;
  }
  if (u.mode !== 0) {
    e.facing = iatan2(r.desk.y - e.y, r.desk.x - e.x);
    e.anim = u.mode === 1 ? 'cast' : 'idle';
    if (u.mode === 1) e.animT = imax(0, e.animT) % 12;
    return true;
  }
  if (withinRange(e.x, e.y, r.desk.x, r.desk.y, r.deskR)) {
    // At the desk: it writes on the last seal broken; with none, it waits (and writes the moment one breaks).
    if (lastBroken(w) >= 0) {
      u.mode = 1;
      u.t = s.tick + r.scribe.write;
      ev(s, { t: 'senda', kind: 'write', npc: '', x: e.x, y: e.y, n: u.id });
      sendaCue(s, `writing${++w.writes}`);
    } else {
      u.mode = 2;
      u.t = s.tick;
    }
    return true;
  }
  e.anim = 'walk';
  e.facing = iatan2(r.desk.y - e.y, r.desk.x - e.x);
  stepToward(e, r.desk.x, r.desk.y, px);
  return true;
};

/** Seals broken now, of three. */
export const sealsGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.seals;
  if (!w) return { done: 0, need: 3 };
  return { done: w.seals.filter((x) => x.broken).length, need: w.seals.length };
};

/** Won the three broken at once (a moment after: the cabinet opening); the test lost at `lose` seals sealed again. */
export const sealsResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.seals;
  const w = s.senda?.seals;
  if (!r || !w) return null;
  if (w.done >= 0) return s.tick >= w.done + 18 ? 'won' : null;
  return r.lose > 0 && w.resealed >= r.lose ? 'lost' : null;
};
